import { useState } from 'react'
import {
  Stack, Paper, Text, Button, Group, Textarea, Rating, Divider, useComputedColorScheme,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCircleCheck, IconThumbDown } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'

interface CustomerValidationPanelProps {
  taskRequestId: number
  tenantId: number
  isOwner: boolean
  status: string
  /** Prazo da validação do solicitante (96h, TASK-2789); null quando não gravado. */
  validationDueAt: string | null
}

type EstadoDoPrazo = 'sem_prazo' | 'folga' | 'ultimo_dia' | 'vencido'

/** Distância até o prazo de 96h da validação. "Último dia" = menos de 24h. */
export function estadoDoPrazo(dueIso: string | null | undefined, agora: Date): EstadoDoPrazo {
  if (!dueIso) return 'sem_prazo'
  const due = new Date(dueIso).getTime()
  if (Number.isNaN(due)) return 'sem_prazo'
  const faltaMs = due - agora.getTime()
  if (faltaMs <= 0) return 'vencido'
  if (faltaMs < 24 * 3_600_000) return 'ultimo_dia'
  return 'folga'
}

// Info = inovareTeal (marca), Warning = yellow, Danger = red — DS §2.3.
const COR_DO_PRAZO: Record<EstadoDoPrazo, 'inovareTeal' | 'yellow' | 'red'> = {
  sem_prazo: 'inovareTeal', folga: 'inovareTeal', ultimo_dia: 'yellow', vencido: 'red',
}

export interface CoresDaFaixa { fundo: string; borda: string; titulo: string; texto: string; prazo: string }

/**
 * Cores da faixa por estado do prazo E por tema (review da Onda 3: `{cor}.0` era hex
 * claro fixo, e no escuro o texto claro do tema caía sobre ele a ~1,45:1).
 *
 * Fundo: `--ai-bg-subtle` no teal (muda com o tema: #e6f3f5 / #1e3a4c) e
 * `--mantine-color-{cor}-light` em amarelo/vermelho (o Mantine já a gera por tema).
 * O `-light` é translúcido e compõe sobre o fundo da PÁGINA (`--ai-body-bg`, #f2f3f4
 * / #0a1f28), não sobre o cartão. Texto AA (>= 4,5:1) sobre o composto, valores
 * resolvidos em `__tests__/CustomerValidationPanel.tema.test.tsx`:
 *  - título: Azul da marca no claro (6,7–7,5:1); no escuro o Azul dá 1,4:1, então
 *    texto primário (#e6f3f5, 10,5–12,4:1);
 *  - prazo no claro, vencido ou último dia: nenhum vermelho nem amarelo do DS chega a
 *    AA sobre o composto (red.9 dá 4,3:1, yellow.9 2,8:1), então o texto é o
 *    primário em negrito (9,9–10,5:1) e o estado fica no fundo e na borda da cor;
 *  - prazo no escuro: red.3 (7,6:1) no vencido, yellow.3 (9,4:1) no último dia.
 */
export function coresDaFaixa(estado: EstadoDoPrazo, esquema: 'light' | 'dark'): CoresDaFaixa {
  const cor = COR_DO_PRAZO[estado]
  const texto = 'var(--ai-text-primary)'
  const escuro = esquema === 'dark'
  const fundo = cor === 'inovareTeal' ? 'var(--ai-bg-subtle)' : `var(--mantine-color-${cor}-light)`
  const borda = cor === 'inovareTeal' ? 'var(--ai-border-medium)' : `var(--mantine-color-${cor}-outline)`
  const titulo = escuro ? texto : 'var(--ai-brand-azul)'
  let prazo = texto
  if (estado === 'vencido' && escuro) prazo = 'var(--mantine-color-red-3)'
  if (estado === 'ultimo_dia' && escuro) prazo = 'var(--mantine-color-yellow-3)'
  return { fundo, borda, titulo, texto, prazo }
}

function formatarPrazo(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short',
  })
}

/**
 * Painel de validação do SOLICITANTE (chamados v2). Renderiza somente quando o
 * chamado está em 'pending_customer_validation' E o usuário logado é o dono do
 * chamado. Permite Validar (encerra + CSAT opcional) ou Não-Validar (devolve
 * para atendimento com justificativa obrigatória).
 */
export function CustomerValidationPanel({
  taskRequestId, tenantId, isOwner, status, validationDueAt,
}: CustomerValidationPanelProps) {
  const queryClient = useQueryClient()
  const esquema = useComputedColorScheme('light')
  const [csat, setCsat] = useState(0)
  const [csatComment, setCsatComment] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [justification, setJustification] = useState('')

  const basePath = `/v1/tenants/${tenantId}/workflow/${taskRequestId}`

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['chamado', String(taskRequestId)] })
    // A aba "Aguardando sua validação" e o número do menu saem depois de validar.
    invalidarPendenciasDoChamado(queryClient)
  }

  const validateMutation = useMutation({
    mutationFn: async () => {
      await api.post(`${basePath}/validar`, {
        csat: csat > 0 ? csat : undefined,
        csat_comment: csatComment.trim() || undefined,
      })
    },
    onSuccess: () => {
      invalidate()
      notifications.show({ color: 'teal', message: 'Chamado validado. Obrigado!' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao validar o chamado.') })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      await api.post(`${basePath}/nao-validar`, { justification: justification.trim() })
    },
    onSuccess: () => {
      invalidate()
      setRejecting(false)
      setJustification('')
      notifications.show({ color: 'orange', message: 'Chamado devolvido para atendimento.' })
    },
    onError: (err: unknown) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao devolver o chamado.') })
    },
  })

  if (status !== 'pending_customer_validation' || !isOwner) return null

  const estado = estadoDoPrazo(validationDueAt, new Date())
  // Faixa no topo do detalhe (spec 2026-09-27 §6.2); cores por tema em `coresDaFaixa`.
  // O Azul vem do token `--ai-brand-azul` (#025468). Desde a TASK-12193 o shade 7
  // do tema também é o Azul; o token segue sendo a fonte por nomear a cor da marca.
  const cores = coresDaFaixa(estado, esquema)
  return (
    <Paper p="md" radius="md" withBorder bg={cores.fundo}
      style={{ borderColor: cores.borda }}>
      <Stack gap="sm">
        <Text size="sm" fw={600} c={cores.titulo}>Aguardando sua validação</Text>
        <Text size="sm" c={cores.texto}>
          O atendimento foi finalizado. Confirme se o chamado foi resolvido ou
          devolva-o com uma justificativa.
        </Text>
        {validationDueAt && estado !== 'sem_prazo' && (
          <Text size="sm" c={cores.prazo} fw={estado === 'folga' ? 400 : 600}>
            Prazo: <Text span ff="monospace" size="sm">{formatarPrazo(validationDueAt)}</Text>
            {estado === 'vencido'
              ? ' — venceu; o chamado será fechado automaticamente.'
              : ' — sem resposta até lá, o chamado fecha sozinho.'}
          </Text>
        )}

        {!rejecting && (
          <Stack gap="xs">
            <Text size="xs" c={cores.texto}>Como você avalia o atendimento? (opcional)</Text>
            <Rating value={csat} onChange={setCsat} />
            <Textarea
              size="xs"
              placeholder="Comentário sobre o atendimento (opcional)"
              value={csatComment}
              onChange={(e) => setCsatComment(e.currentTarget.value)}
              rows={2}
              maxLength={1000}
            />
          </Stack>
        )}

        {rejecting && (
          <Textarea
            size="xs"
            label="Justificativa (obrigatória)"
            placeholder="Descreva por que o chamado não foi resolvido..."
            value={justification}
            onChange={(e) => setJustification(e.currentTarget.value)}
            rows={3}
            maxLength={1000}
            required
          />
        )}

        <Divider />

        <Group gap="xs">
          {!rejecting ? (
            <>
              <Button
                size="xs"
                color="teal"
                leftSection={<IconCircleCheck size={14} />}
                onClick={() => validateMutation.mutate()}
                loading={validateMutation.isPending}
              >
                Validado
              </Button>
              <Button
                size="xs"
                color="orange"
                variant="light"
                leftSection={<IconThumbDown size={14} />}
                onClick={() => setRejecting(true)}
              >
                Não Validado
              </Button>
            </>
          ) : (
            <>
              <Button
                size="xs"
                color="orange"
                leftSection={<IconThumbDown size={14} />}
                disabled={!justification.trim()}
                onClick={() => rejectMutation.mutate()}
                loading={rejectMutation.isPending}
              >
                Devolver ao atendimento
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => { setRejecting(false); setJustification('') }}
              >
                Cancelar
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Paper>
  )
}
